/**
 * The network — Casey's projects and partner sites.
 *
 * One source of truth for the three places these links appear: the footer's
 * link band, the sidebar's drop-down menu, and the /projects page. Taglines are
 * taken from each site's own words where it has them.
 */

export type ProjectGroupId =
  | "mclaw"
  | "uplaw"
  | "rightsy"
  | "stampo"
  | "trulove"
  | "mc3"
  | "learned"
  | "personal";

export interface Project {
  /** Slug — also the anchor id on the /projects page. */
  id: string;
  /** Full name, as the project bills itself. */
  name: string;
  /** Short label for tight spaces (sidebar, footer columns). */
  short: string;
  /** Bare hostname, shown under each entry on /projects. */
  domain: string;
  url: string;
  group: ProjectGroupId;
  /** One line — the project's own pitch. */
  tagline: string;
  /** A short paragraph for the /projects page. */
  blurb: string;
  /** Set on csmckay.us: this very site, so links point home rather than out. */
  current?: boolean;
  /** Not live yet — flagged rather than quietly linked to nothing. */
  upcoming?: boolean;
}

export interface ProjectGroup {
  id: ProjectGroupId;
  label: string;
  note: string;
}

/**
 * Families, in the order they're listed everywhere. The two with sub-projects
 * lead — the law practice and the UpLaw stack — then the standalone products,
 * each filed under the kind of work it does, and the personal ones last.
 */
export const PROJECT_GROUPS: ProjectGroup[] = [
  { id: "mclaw", label: "MC Law", note: "The law practice, and the tools around it." },
  { id: "uplaw", label: "UpLaw", note: "Legal research, citations, and AI tooling." },
  { id: "rightsy", label: "IP Rights", note: "Trademarks, copyrights, and patents." },
  { id: "stampo", label: "PDF Tools", note: "Everyday document work." },
  { id: "trulove", label: "Trading", note: "Markets, traded by software." },
  { id: "mc3", label: "Engineering", note: "Where the rest of this gets built." },
  { id: "learned", label: "Arbitration", note: "Deciding disputes, automatically." },
  { id: "personal", label: "Personal", note: "The ones I make for myself." },
];

export const PROJECTS: Project[] = [
  {
    id: "mc-law",
    name: "MC Law, PLLC",
    short: "MC Law",
    domain: "mclaw.io",
    url: "https://mclaw.io",
    group: "mclaw",
    tagline: "Intellectual property & technology law.",
    blurb:
      "My law practice. Trademarks, patents, trade secrets, copyright, privacy, " +
      "and the technology transactions that sit underneath them — for founders " +
      "and companies building in AI, blockchain, and clean tech. The pitch is " +
      "simple: counsel who actually understand what you've built.",
  },
  {
    id: "mc-law-news",
    name: "MC Law News",
    short: "MC Law News",
    domain: "news.mclaw.io",
    url: "https://news.mclaw.io",
    group: "mclaw",
    tagline: "Legal news, ranked by the community.",
    blurb:
      "A community-ranked feed of legal news. Readers submit stories and vote " +
      "them up, so what lands at the top is what practitioners think matters " +
      "this week — not what an editor decided to feature.",
  },
  {
    id: "silq",
    name: "Silq",
    short: "Silq",
    domain: "silq.mclaw.io",
    url: "https://silq.mclaw.io",
    group: "mclaw",
    tagline: "Legal time & matter management.",
    blurb:
      "Practice management without the bloat. Silq handles the two things a " +
      "small firm cannot afford to get wrong — tracking time and organizing " +
      "matters — and stays out of the way for everything else.",
  },
  {
    id: "uplaw",
    name: "UpLaw",
    short: "UpLaw",
    domain: "uplaw.us",
    url: "https://uplaw.us",
    group: "uplaw",
    tagline: "Legal research & virtual legal services.",
    blurb:
      "A legal research platform paired with virtual legal services: search " +
      "authority in one place, then get the work done. The research tools and " +
      "the practitioner behind them, on the same site.",
  },
  {
    id: "alfie",
    name: "A.L.F.I.E.",
    short: "A.L.F.I.E.",
    domain: "alfie.uplaw.us",
    url: "https://alfie.uplaw.us",
    group: "uplaw",
    tagline: "AI legal research & drafting.",
    blurb:
      "The AI layer over UpLaw. Ask a research question in plain language, and " +
      "turn the answer into a first draft. Built to take the grind out of " +
      "research and drafting without pretending to replace judgment.",
  },
  {
    id: "upcite",
    name: "UpCite",
    short: "UpCite",
    domain: "upcite.uplaw.us",
    url: "https://upcite.uplaw.us",
    group: "uplaw",
    tagline: "Citation resolver & extractor.",
    blurb:
      "Point it at a brief or an opinion and it pulls out every citation, " +
      "normalizes it, and resolves it to the authority it points at. Unglamorous " +
      "plumbing, and the difference between a clean table of authorities and an " +
      "afternoon lost to one.",
  },
  {
    id: "uplaw-news",
    name: "UpLaw News",
    short: "UpLaw News",
    domain: "news.uplaw.us",
    url: "https://news.uplaw.us",
    group: "uplaw",
    tagline: "Legal news, ranked by the community.",
    blurb:
      "The same community-ranked format, pointed at legal technology and " +
      "practice. Submit a story, vote on the rest, and the front page sorts " +
      "itself out.",
  },
  {
    id: "rightsy",
    name: "Rightsy",
    short: "Rightsy",
    domain: "rightsy.io",
    url: "https://rightsy.io",
    group: "rightsy",
    tagline: "Protect your rights.",
    blurb:
      "A search engine and analysis tools for trademarks, copyrights, and " +
      "patents. Find what's already registered, see what's standing in your " +
      "way, and protect your rights.",
  },
  {
    id: "stampo",
    name: "Stampo",
    short: "Stampo",
    domain: "stampo.org",
    url: "https://stampo.org",
    group: "stampo",
    tagline: "PDF tools for everyday documents.",
    blurb:
      "The everyday PDF jobs, done in the browser: merge, split, rotate, " +
      "compress, convert, stamp. All the small tasks that turn into a " +
      "twenty-minute detour when the only thing that can do them is desktop " +
      "software you don't have installed.",
  },
  {
    id: "trulove",
    name: "TruLove",
    short: "TruLove",
    domain: "trulove.io",
    url: "https://trulove.io",
    group: "trulove",
    tagline: "AI algorithmic trading platform.",
    blurb:
      "An algorithmic trading platform with AI doing the deciding — strategies, " +
      "backtests to keep them honest, and the infrastructure to run the whole " +
      "thing without anyone watching a chart. Equal parts market experiment and " +
      "excuse to write very careful software.",
  },
  {
    id: "mc3",
    name: "MC3",
    short: "MC3",
    domain: "mc3.dev",
    url: "https://mc3.dev",
    group: "mc3",
    tagline: "Software development shop.",
    blurb:
      "The shop most of this list comes out of. MC3 builds and ships the " +
      "products here, and takes on development work for people who need " +
      "something built properly the first time.",
  },
  {
    id: "learned",
    name: "Learned",
    short: "Learned",
    domain: "learned.cc",
    url: "https://learned.cc",
    group: "learned",
    tagline: "Automated judge & arbitrator. Coming soon.",
    blurb:
      "An AI platform for deciding disputes: read the record, weigh both sides, " +
      "and hand down a reasoned decision — aimed at the disagreements that cost " +
      "more to litigate than they are worth. The newest thing on the bench, " +
      "appropriately enough, and not public yet.",
    upcoming: true,
  },
  {
    id: "csmckay",
    name: "C.S. McKay",
    short: "C.S. McKay",
    domain: "csmckay.us",
    url: "https://csmckay.us",
    group: "personal",
    tagline: "This site — photos, videos, and field notes.",
    blurb:
      "You're on it. A personal archive of photographs, video, and journal " +
      "entries: everything catalogued, numbered, and searchable, the way a small " +
      "library would do it.",
    current: true,
  },
  {
    id: "thalassa",
    name: "Tides of Thalassa",
    short: "Tides of Thalassa",
    domain: "thalassa.csmckay.us",
    url: "https://thalassa.csmckay.us",
    group: "personal",
    tagline: "A real-time strategy game.",
    blurb:
      "A real-time strategy game set at sea, built in the evenings — fleets, " +
      "weather, and a coastline worth fighting over.",
  },
];

/** The projects in one family, in listed order. */
export function projectsInGroup(group: ProjectGroupId): Project[] {
  return PROJECTS.filter((project) => project.group === group);
}
